import { LuIcon, Menu } from "@overmap-ai/blocks"
import { ProcedureStepFieldValuesReviewType, EMPTY_ARRAY } from "@overmap-ai/common"
import { use, useCallback } from "react"

import {
	MultiSelectItemTemplate,
	SelectAllItemTemplate,
	SelectItemTemplate,
} from "../../../components/MenuGroups/constants"
import { PROCEDURE_STEP_FIELD_VALUES_REVIEW_TYPES } from "../../../constants"
import { modelIconMapping } from "../../../mappings/icons"
import { procedureStepFieldValuesReviewTypeData } from "../../../mappings/procedures"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { ProcedureStepReviewStatusFilterDef } from "./ProcedureStepReviewStatusFilterDef"

type ProcedureStepReviewStatusFilterCellProps = FilterDefRenderProps<ProcedureStepReviewStatusFilterDef>

export const ProcedureStepReviewStatusFilterCell = (props: ProcedureStepReviewStatusFilterCellProps) => {
	const { filter, filterDef, filterManager } = props

	const { size } = use(FilterRendererContext)
	const { procedureStep } = filterDef
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const handleValueChange = useCallback(
		(value?: ProcedureStepFieldValuesReviewType | null) => {
			filter.setFilterValue(value)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(values: (ProcedureStepFieldValuesReviewType | null)[]) => {
			if (values.length > 0) {
				filter.setFilterValue(values)
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsModifierFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				const getLabelUi = () => {
					switch (equalsModifierFilterValue) {
						case undefined:
							return "..."
						case null:
							return (
								<>
									<LuIcon icon={modelIconMapping.none} />
									None
								</>
							)
						default:
							const { statusLabel, icon, color } =
								procedureStepFieldValuesReviewTypeData[equalsModifierFilterValue]

							return (
								<>
									<LuIcon icon={icon} color={color} />
									{statusLabel}
								</>
							)
					}
				}

				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton className="truncate">{getLabelUi()}</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.Scroll>
								<Menu.SelectGroup<ProcedureStepFieldValuesReviewType | null>
									required={false}
									value={equalsModifierFilterValue}
									onValueChange={handleValueChange}
								>
									{PROCEDURE_STEP_FIELD_VALUES_REVIEW_TYPES.map((reviewType) => {
										const { statusLabel, icon, color } =
											procedureStepFieldValuesReviewTypeData[reviewType]

										return (
											<SelectItemTemplate<ProcedureStepFieldValuesReviewType | null>
												key={reviewType}
												value={reviewType}
											>
												<LuIcon icon={icon} color={color} />
												{statusLabel}
											</SelectItemTemplate>
										)
									})}

									<SelectItemTemplate<ProcedureStepFieldValuesReviewType | null> value={null}>
										<LuIcon icon={modelIconMapping.none} />
										None
									</SelectItemTemplate>
								</Menu.SelectGroup>
							</Menu.Scroll>
						</Menu.Content>
					</Menu.Root>
				)
			}
			case "includes":
			case "excludes": {
				const includesModifier = filterDef.modifiers.includes
				const includesModifierFilterValue =
					filterValue !== undefined && includesModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				const getLabelUi = () => {
					switch ((includesModifierFilterValue ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1: {
							const reviewType = includesModifierFilterValue![0]

							switch (reviewType) {
								case null:
									return (
										<>
											<LuIcon icon={modelIconMapping.none} />
											None
										</>
									)
								default: {
									const { statusLabel, icon, color } =
										procedureStepFieldValuesReviewTypeData[reviewType]

									return (
										<>
											<LuIcon icon={icon} color={color} />
											{statusLabel}
										</>
									)
								}
							}
						}
						default:
							return `${includesModifierFilterValue!.length} options`
					}
				}

				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton className="truncate">
								<span className="flex items-center gap-2 truncate">{getLabelUi()}</span>
							</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.Scroll>
								<Menu.MultiSelectGroup<ProcedureStepFieldValuesReviewType | null>
									values={includesModifierFilterValue ?? EMPTY_ARRAY}
									onValuesChange={handleValuesChange}
								>
									<SelectAllItemTemplate>Select all</SelectAllItemTemplate>
									{PROCEDURE_STEP_FIELD_VALUES_REVIEW_TYPES.map((reviewType) => {
										const { statusLabel, icon, color } =
											procedureStepFieldValuesReviewTypeData[reviewType]

										return (
											<MultiSelectItemTemplate<ProcedureStepFieldValuesReviewType | null>
												key={reviewType}
												value={reviewType}
											>
												<LuIcon icon={icon} color={color} />
												{statusLabel}
											</MultiSelectItemTemplate>
										)
									})}
									<MultiSelectItemTemplate<ProcedureStepFieldValuesReviewType | null> value={null}>
										<LuIcon icon={modelIconMapping.none} />
										None
									</MultiSelectItemTemplate>
								</Menu.MultiSelectGroup>
							</Menu.Scroll>
						</Menu.Content>
					</Menu.Root>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.stage} color={procedureStep.color} fill={procedureStep.color} />
				{procedureStep.name} - Review
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
